import { Promo } from '@/modules/Promo/types'
import {
    PROMO_STATUS_OPTIONS,
    PROMO_TYPE_OPTIONS,
} from '@/modules/Promo/constants'
import { useTranslation } from 'react-i18next'
import { GridInformationWithHookForm } from 'vnetwork-ui'
import SettingIcons from '@/icons/SettingIcons'
import dayjs from 'dayjs'
import { updatePromo } from '@/modules/Promo/services'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import * as yup from 'yup'
import CellStatus, { Color } from '@/components/GokuDataGrid/CellStatus'

interface OverviewProps {
    promo?: Promo
    refetch?: () => void
}

const updatePromoSchema = yup.object({
    name: yup.string().required('Name is required'),
    description: yup.string(),
    discount_value: yup.number().min(0),
    max_discount: yup.number().min(0).nullable(),
    max_uses: yup.number().min(0).nullable(),
    max_uses_per_customer: yup.number().min(0),
    status: yup.string(),
})

const formatCurrency = (value: number, currency = 'VND') => {
    return new Intl.NumberFormat('vi-VN', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
    }).format(value)
}

const Overview: React.FC<OverviewProps> = ({ promo, refetch }) => {
    const { t } = useTranslation()

    const handleEdit = async (values: any) => {
        if (!promo) return
        return updatePromo({
            promo_id: promo.promo_id,
            payload: values,
        })
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        { mutationFn: handleEdit },
        {
            reactQueryKeys: QUERY_KEYS.PROMOS,
            successMessage: t('updated-success'),
            errorMessage: t('Something went wrong'),
            onSuccess: () => {
                refetch?.()
            },
        }
    )

    if (!promo) return null

    const typeOption = PROMO_TYPE_OPTIONS(t)?.find(
        (item) => item?.value === promo.promo_type
    )
    const statusOption = PROMO_STATUS_OPTIONS(t)?.find(
        (item) => item?.value === promo.status
    )

    return (
        <div className="space-y-4">
            <GridInformationWithHookForm
                loading={isPending}
                onEdit={mutateAsync}
                schema={updatePromoSchema as any}
                data={[
                    {
                        label: t('promo_id'),
                        value: promo.promo_id,
                    },
                    {
                        label: t('code'),
                        value: (
                            <span className="font-mono font-semibold">
                                {promo.code}
                            </span>
                        ),
                    },
                    {
                        label: t('name'),
                        value: promo.name,
                        formItemProps: {
                            name: 'name',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('description'),
                        value: promo.description || '-',
                        formItemProps: {
                            name: 'description',
                            type: 'textfield',
                        },
                    },
                    {
                        label: t('type'),
                        value: typeOption?.label || promo.promo_type,
                    },
                    {
                        label: t('discount_value'),
                        value:
                            promo.promo_type === 'percentage'
                                ? `${promo.discount_value}%`
                                : formatCurrency(
                                      promo.discount_value,
                                      promo.currency
                                  ),
                        formItemProps: {
                            name: 'discount_value',
                            type: 'number',
                        },
                    },
                    {
                        label: t('max_discount'),
                        value: promo.max_discount
                            ? formatCurrency(promo.max_discount, promo.currency)
                            : t('no_limit'),
                        formItemProps: {
                            name: 'max_discount',
                            type: 'number',
                        },
                    },
                    {
                        label: t('uses'),
                        value: `${promo.current_uses}${
                            promo.max_uses ? ` / ${promo.max_uses}` : ''
                        }`,
                    },
                    {
                        label: t('max_uses'),
                        value: promo.max_uses || t('unlimited'),
                        formItemProps: {
                            name: 'max_uses',
                            type: 'number',
                        },
                    },
                    {
                        label: t('max_uses_per_customer'),
                        value: promo.max_uses_per_customer || 1,
                        formItemProps: {
                            name: 'max_uses_per_customer',
                            type: 'number',
                        },
                    },
                    {
                        label: t('min_order_amount'),
                        value: promo.min_order_amount
                            ? formatCurrency(
                                  promo.min_order_amount,
                                  promo.currency
                              )
                            : t('no_minimum'),
                    },
                    {
                        label: t('new_customers_only'),
                        value: promo.new_customers_only ? t('yes') : t('no'),
                    },
                    {
                        label: t('valid_from'),
                        value: promo.valid_from
                            ? dayjs(promo.valid_from).format('DD/MM/YYYY HH:mm')
                            : '-',
                    },
                    {
                        label: t('valid_until'),
                        value: promo.valid_until
                            ? dayjs(promo.valid_until).format(
                                  'DD/MM/YYYY HH:mm'
                              )
                            : t('no_expiry'),
                    },
                    {
                        label: t('affiliate'),
                        value: promo.referrer_name || t('system'),
                    },
                    {
                        label: t('status'),
                        value: (
                            <CellStatus
                                label={statusOption?.label || promo.status}
                                color={statusOption?.color as Color}
                                className="capitalize"
                            />
                        ),
                        formItemProps: {
                            name: 'status',
                            type: 'select',
                            options: PROMO_STATUS_OPTIONS(t),
                        },
                    },
                    {
                        label: t('created_at'),
                        value: promo.created_at
                            ? dayjs(promo.created_at).format(
                                  'DD/MM/YYYY HH:mm:ss'
                              )
                            : '-',
                    },
                    {
                        label: t('updated_at'),
                        value: promo.updated_at
                            ? dayjs(promo.updated_at).format(
                                  'DD/MM/YYYY HH:mm:ss'
                              )
                            : '-',
                    },
                ]}
                className="mt-4"
                head={{
                    title: t('promo_details'),
                    icon: <SettingIcons />,
                }}
                defaultFormValues={promo}
            />
        </div>
    )
}

export default Overview
